/**
 * 钱包Redux状态管理
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WalletApiService, WalletInfo, RechargeRecord, SpendingRecord, LearningStats } from '../services/walletApi';

export interface WalletState {
  // 钱包信息
  walletInfo: WalletInfo | null;
  walletLoading: boolean;
  walletError: string | null;

  // 充值记录
  rechargeRecords: RechargeRecord[];
  rechargeLoading: boolean;
  rechargeError: string | null;
  rechargeHasMore: boolean;

  // 消费记录
  spendingRecords: SpendingRecord[];
  spendingLoading: boolean;
  spendingError: string | null;
  spendingHasMore: boolean;

  // 学习统计
  learningStats: LearningStats | null;
  statsLoading: boolean;
  statsError: string | null;

  // UI状态
  refreshing: boolean;
}

const initialState: WalletState = {
  walletInfo: null,
  walletLoading: false,
  walletError: null,

  rechargeRecords: [],
  rechargeLoading: false,
  rechargeError: null,
  rechargeHasMore: true,

  spendingRecords: [],
  spendingLoading: false,
  spendingError: null,
  spendingHasMore: true,

  learningStats: null,
  statsLoading: false,
  statsError: null,

  refreshing: false,
};

// 异步操作

/**
 * 获取钱包信息
 */
export const fetchWalletInfo = createAsyncThunk(
  'wallet/fetchWalletInfo',
  async (_, { rejectWithValue }) => {
    try {
      const walletInfo = await WalletApiService.getWalletInfo();
      return walletInfo;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取钱包信息失败');
    }
  }
);

/**
 * 发起充值
 */
export const initiateRecharge = createAsyncThunk(
  'wallet/initiateRecharge',
  async (
    { amount, paymentMethod }: { amount: number; paymentMethod: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await WalletApiService.rechargeWallet({
        amount,
        payment_method: paymentMethod,
      });

      // 充值请求成功后，刷新充值记录
      dispatch(fetchRechargeRecords({ page: 1, refresh: true }));

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || '充值请求失败');
    }
  }
);

/**
 * 完成充值
 */
export const completeRecharge = createAsyncThunk(
  'wallet/completeRecharge',
  async (
    rechargeId: string,
    { rejectWithValue, dispatch }
  ) => {
    try {
      await WalletApiService.completeRecharge(rechargeId);

      // 完成充值后，刷新钱包信息和充值记录
      dispatch(fetchWalletInfo());
      dispatch(fetchRechargeRecords({ page: 1, refresh: true }));

      return rechargeId;
    } catch (error: any) {
      return rejectWithValue(error.message || '完成充值失败');
    }
  }
);

/**
 * 获取充值记录
 */
export const fetchRechargeRecords = createAsyncThunk(
  'wallet/fetchRechargeRecords',
  async (
    { page = 1, limit = 20, refresh = false }: { page?: number; limit?: number; refresh?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const records = await WalletApiService.getRechargeRecords(page, limit);
      return { records, page, refresh };
    } catch (error: any) {
      return rejectWithValue(error.message || '获取充值记录失败');
    }
  }
);

/**
 * 获取消费记录
 */
export const fetchSpendingRecords = createAsyncThunk(
  'wallet/fetchSpendingRecords',
  async (
    { page = 1, limit = 20, refresh = false }: { page?: number; limit?: number; refresh?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const records = await WalletApiService.getSpendingRecords(page, limit);
      return { records, page, refresh };
    } catch (error: any) {
      return rejectWithValue(error.message || '获取消费记录失败');
    }
  }
);

/**
 * 获取学习统计
 */
export const fetchLearningStats = createAsyncThunk(
  'wallet/fetchLearningStats',
  async (_, { rejectWithValue }) => {
    try {
      const stats = await WalletApiService.getLearningStats();
      return stats;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取学习统计失败');
    }
  }
);

/**
 * 更新学习统计
 */
export const updateLearningStats = createAsyncThunk(
  'wallet/updateLearningStats',
  async (
    date?: string,
    { rejectWithValue, dispatch }
  ) => {
    try {
      await WalletApiService.updateStats(date);

      // 更新完成后，重新获取统计数据
      dispatch(fetchLearningStats());

      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || '更新学习统计失败');
    }
  }
);

/**
 * 刷新所有数据
 */
export const refreshAllData = createAsyncThunk(
  'wallet/refreshAllData',
  async (_, { dispatch }) => {
    await Promise.all([
      dispatch(fetchWalletInfo()),
      dispatch(fetchRechargeRecords({ page: 1, refresh: true })),
      dispatch(fetchSpendingRecords({ page: 1, refresh: true })),
      dispatch(fetchLearningStats()),
    ]);
  }
);

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    // 清除错误状态
    clearErrors: (state) => {
      state.walletError = null;
      state.rechargeError = null;
      state.spendingError = null;
      state.statsError = null;
    },

    // 重置钱包状态
    resetWallet: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // 获取钱包信息
    builder
      .addCase(fetchWalletInfo.pending, (state) => {
        state.walletLoading = true;
        state.walletError = null;
      })
      .addCase(fetchWalletInfo.fulfilled, (state, action: PayloadAction<WalletInfo>) => {
        state.walletLoading = false;
        state.walletInfo = action.payload;
      })
      .addCase(fetchWalletInfo.rejected, (state, action) => {
        state.walletLoading = false;
        state.walletError = action.payload as string;
      });

    // 发起充值
    builder
      .addCase(initiateRecharge.pending, (state) => {
        state.rechargeLoading = true;
        state.rechargeError = null;
      })
      .addCase(initiateRecharge.fulfilled, (state) => {
        state.rechargeLoading = false;
      })
      .addCase(initiateRecharge.rejected, (state, action) => {
        state.rechargeLoading = false;
        state.rechargeError = action.payload as string;
      });

    // 完成充值
    builder
      .addCase(completeRecharge.pending, (state) => {
        state.rechargeLoading = true;
        state.rechargeError = null;
      })
      .addCase(completeRecharge.fulfilled, (state) => {
        state.rechargeLoading = false;
      })
      .addCase(completeRecharge.rejected, (state, action) => {
        state.rechargeLoading = false;
        state.rechargeError = action.payload as string;
      });

    // 获取充值记录
    builder
      .addCase(fetchRechargeRecords.pending, (state) => {
        state.rechargeLoading = true;
        state.rechargeError = null;
      })
      .addCase(fetchRechargeRecords.fulfilled, (state, action) => {
        state.rechargeLoading = false;
        const { records, page, refresh } = action.payload;

        if (refresh || page === 1) {
          state.rechargeRecords = records;
        } else {
          state.rechargeRecords = [...state.rechargeRecords, ...records];
        }

        // 如果返回的记录少于请求的数量，说明没有更多数据了
        state.rechargeHasMore = records.length >= 20;
      })
      .addCase(fetchRechargeRecords.rejected, (state, action) => {
        state.rechargeLoading = false;
        state.rechargeError = action.payload as string;
      });

    // 获取消费记录
    builder
      .addCase(fetchSpendingRecords.pending, (state) => {
        state.spendingLoading = true;
        state.spendingError = null;
      })
      .addCase(fetchSpendingRecords.fulfilled, (state, action) => {
        state.spendingLoading = false;
        const { records, page, refresh } = action.payload;

        if (refresh || page === 1) {
          state.spendingRecords = records;
        } else {
          state.spendingRecords = [...state.spendingRecords, ...records];
        }

        // 如果返回的记录少于请求的数量，说明没有更多数据了
        state.spendingHasMore = records.length >= 20;
      })
      .addCase(fetchSpendingRecords.rejected, (state, action) => {
        state.spendingLoading = false;
        state.spendingError = action.payload as string;
      });

    // 获取学习统计
    builder
      .addCase(fetchLearningStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchLearningStats.fulfilled, (state, action: PayloadAction<LearningStats>) => {
        state.statsLoading = false;
        state.learningStats = action.payload;
      })
      .addCase(fetchLearningStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload as string;
      });

    // 更新学习统计
    builder
      .addCase(updateLearningStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(updateLearningStats.fulfilled, (state) => {
        state.statsLoading = false;
      })
      .addCase(updateLearningStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload as string;
      });

    // 刷新所有数据
    builder
      .addCase(refreshAllData.pending, (state) => {
        state.refreshing = true;
      })
      .addCase(refreshAllData.fulfilled, (state) => {
        state.refreshing = false;
      })
      .addCase(refreshAllData.rejected, (state) => {
        state.refreshing = false;
      });
  },
});

export const { clearErrors, resetWallet } = walletSlice.actions;

// Selectors
export const selectWalletInfo = (state: { wallet: WalletState }) => state.wallet.walletInfo;
export const selectWalletLoading = (state: { wallet: WalletState }) => state.wallet.walletLoading;
export const selectWalletError = (state: { wallet: WalletState }) => state.wallet.walletError;

export const selectRechargeRecords = (state: { wallet: WalletState }) => state.wallet.rechargeRecords;
export const selectRechargeLoading = (state: { wallet: WalletState }) => state.wallet.rechargeLoading;
export const selectRechargeError = (state: { wallet: WalletState }) => state.wallet.rechargeError;
export const selectRechargeHasMore = (state: { wallet: WalletState }) => state.wallet.rechargeHasMore;

export const selectSpendingRecords = (state: { wallet: WalletState }) => state.wallet.spendingRecords;
export const selectSpendingLoading = (state: { wallet: WalletState }) => state.wallet.spendingLoading;
export const selectSpendingError = (state: { wallet: WalletState }) => state.wallet.spendingError;
export const selectSpendingHasMore = (state: { wallet: WalletState }) => state.wallet.spendingHasMore;

export const selectLearningStats = (state: { wallet: WalletState }) => state.wallet.learningStats;
export const selectStatsLoading = (state: { wallet: WalletState }) => state.wallet.statsLoading;
export const selectStatsError = (state: { wallet: WalletState }) => state.wallet.statsError;

export const selectRefreshing = (state: { wallet: WalletState }) => state.wallet.refreshing;

export default walletSlice.reducer;
